import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BaseServices } from '../../kuba.services';
import { Rights, Roles } from '../../../_models/feature';
import { AppraisalMeetingService } from './meetings/services/appraisal-meeting.service';
import { AppraisalAssignmentRequestDto } from './settings/models/appraisal-assignment-request.dto';
import { AppraisalRelationshipStatus } from './settings/models/appraisal-relationship-status.enum';
import { AppraisalSettingsService } from './settings/services/appraisal-settings.service';

@Component({
  selector: 'employee-dialog-meetings',
  templateUrl: './employee-dialog-meetings.component.html',
  styleUrls: ['./employee-dialog-meetings.component.scss'],
  standalone: false
})
export class EmployeeDialogMeetingsComponent implements OnInit, OnDestroy {
  //#region Variables
  activeTab: string = 'list';
  employeeId: number;
  businessId: number;
  guideReady: boolean = false;
  // Manager-view availability; administrative permissions are kept separately below.
  isManager: boolean = false;
  initialStatusFilter: string = '';
  initialViewScope: string = '';
  canViewAppraisals: boolean = false;
  canViewTasks: boolean = false;
  canManageTasks: boolean = false;
  canManageTemplates: boolean = false;
  hasAppraisalsForCurrentUser: boolean = false;
  hasPendingRequests: boolean = false;
  loading: boolean = false;
  // Visible when the employee lands on the page with at least one pending manager-access
  // request. Belt-and-suspenders alongside the auto-tab-switch: the modal blocks the user's
  // attention so the request can't be missed if they happen to scan past the active tab.
  showPendingRequestPopup: boolean = false;
  private pendingPopupShownThisSession: boolean = false;
  private hasExplicitTabRequest: boolean = false;
  private hasUserSelectedTab: boolean = false;
  private requestedEntryTab: string | null = null;

  private subscriptions: Subscription[] = [];
  //#endregion

  //#region Constructor
  constructor(
    private route: ActivatedRoute,
    private appraisalMeetingService: AppraisalMeetingService,
    private appraisalSettingsService: AppraisalSettingsService
  ) {
    this.businessId = BaseServices.BusinessId;
    this.employeeId = +BaseServices.FeatureKey;
  }
  //#endregion

  //#region Lifecycle Hooks
  ngOnInit(): void {
    document.querySelector('body')?.classList.remove('opened');

    // Get route parameters
    this.subscriptions.push(
      this.route.params.subscribe(params => {
        if (params['eid']) {
          this.employeeId = +params['eid'];
        }
      })
    );

    // Get query parameters for active tab, optional status filter, and optional viewScope
    this.subscriptions.push(
      this.route.queryParams.subscribe(params => {
        const requestedTab = params['tab']
          ? this.normalizeEntryTab(params['tab'])
          : null;
        this.hasExplicitTabRequest = !!requestedTab;
        this.hasUserSelectedTab = false;
        this.requestedEntryTab = requestedTab;
        this.activeTab = requestedTab || 'list';
        this.initialStatusFilter = params['status'] ?? '';
        this.initialViewScope = ['manager', 'employee'].includes(params['viewScope'])
          ? params['viewScope']
          : '';
        if (this.guideReady) {
          this.enforceTabAccess();
        }
      })
    );

    // Check user permissions
    this.checkPermissions();
    this.loadRequestTabVisibility();

    if (Number(BaseServices.roleId) === Roles.EDITOR) {
      this.resolveEditorManagerAccess();
    } else {
      this.loadMyAppraisalAvailability();
      this.enforceTabAccess();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
  //#endregion

  //#region Methods
  /**
   * Check user permissions for appraisals features
   */
  checkPermissions(): void {
    const roleId = Number(BaseServices.roleId);

    const managerRoleIds = [Roles.ADMIN, Roles.PORTAL];

    // Admins and Portal users have full access.
    if (managerRoleIds.includes(roleId)) {
      this.canViewAppraisals = true;
      this.canViewTasks = true;
      this.canManageTasks = true;
      this.canManageTemplates = true;
      this.isManager = true;
      this.hasAppraisalsForCurrentUser = false;
      this.guideReady = false;
      return;
    }

    // Editors require appraisal-manager assignment to manage templates/settings.
    if (roleId === Roles.EDITOR) {
      this.canViewAppraisals = true;
      this.canViewTasks = true;
      this.canManageTasks = false;
      this.canManageTemplates = false;
      this.isManager = false;
      this.hasAppraisalsForCurrentUser = false;
      this.guideReady = false;
      return;
    }

    // User role - check specific rights
    if (roleId === Roles.USER) {
      // Check if user is a manager (has department management rights)
      this.isManager = BaseServices.checkRightsForAccess(
        Rights.EDIT_DEPARTMENT_EMPLOYEE_RIGHTS,
        roleId
      );

      // All users can view their own appraisals
      this.canViewAppraisals = true;
      this.canViewTasks = true;

      // Managers can manage tasks and templates
      this.canManageTasks = this.isManager;
      this.canManageTemplates = this.isManager;

      if (this.isManager) {
        this.hasAppraisalsForCurrentUser = false;
        this.guideReady = false;
      }
    }

    // Guests can review their own requests and meetings they participate in.
    if (roleId === Roles.GUEST) {
      this.canViewAppraisals = true;
      this.canViewTasks = true;
      this.canManageTasks = false;
      this.canManageTemplates = false;
      this.isManager = false;
      this.hasAppraisalsForCurrentUser = false;
      this.guideReady = false;
    }
  }

  private resolveEditorManagerAccess(): void {
    this.guideReady = false;

    this.subscriptions.push(
      this.appraisalSettingsService.canManageSettings(this.businessId).subscribe({
        next: canManage => {
          this.isManager = canManage;
          this.canViewTasks = true;
          this.canManageTasks = canManage;
          this.canManageTemplates = canManage;
          this.loadMyAppraisalAvailability();
        },
        error: () => {
          this.isManager = false;
          this.canViewTasks = true;
          this.canManageTasks = false;
          this.canManageTemplates = false;
          this.loadMyAppraisalAvailability();
        }
      })
    );
  }

  get guideStorageRoleKey(): string {
    return this.isManager ? 'manager' : 'employee';
  }

  /**
   * Resolves both meeting views independently of permission to administer settings.
   * The list API applies the current user's participant and business boundaries.
   */
  loadMyAppraisalAvailability(): void {
    if (!this.canViewAppraisals) {
      this.guideReady = true;
      return;
    }

    this.loading = true;

    const loadScope = (viewScope: 'manager' | 'employee') =>
      this.appraisalMeetingService.getPagedList(this.businessId, {
        pageNumber: 1,
        pageSize: 1,
        sortColumn: 'CreatedOn',
        sortDirection: 'DESC',
        viewScope
      }).pipe(catchError(() => of({ TotalCount: 0 })));

    this.subscriptions.push(
      forkJoin({ manager: loadScope('manager'), employee: loadScope('employee') })
        .subscribe({
          next: ({ manager, employee }) => {
            this.isManager = this.isManager || manager.TotalCount > 0;
            this.hasAppraisalsForCurrentUser = employee.TotalCount > 0;
            this.applyDefaultTabSelection();
            this.loading = false;
            this.guideReady = true;
          }
        })
    );
  }

  private enforceTabAccess(): void {
    const requestedTab = this.requestedEntryTab === 'my-meetings' &&
      !this.isManager && this.hasAppraisalsForCurrentUser
      ? 'list'
      : this.requestedEntryTab;
    if (
      !this.hasUserSelectedTab &&
      requestedTab &&
      this.isTabAccessible(requestedTab)
    ) {
      this.activeTab = requestedTab;
      return;
    }

    if (!this.isTabAccessible(this.activeTab)) {
      this.activeTab = this.getDefaultAccessibleTab();
    }
  }

  onRequestsUpdated(requests: AppraisalAssignmentRequestDto[]): void {
    this.updatePendingRequestVisibility(requests);
  }

  /**
   * Switch to a different tab
   * @param tabName Tab to activate
   */
  switchTab(tabName: string): void {
    this.hasUserSelectedTab = true;
    this.hasExplicitTabRequest = false;
    this.requestedEntryTab = null;
    this.initialViewScope = '';
    this.initialStatusFilter = '';
    this.activeTab = tabName;
    this.enforceTabAccess();
  }

  /**
   * Check if a tab is active
   * @param tabName Tab name to check
   * @returns True if tab is active
   */
  isTabActive(tabName: string): boolean {
    return this.activeTab === tabName;
  }

  /**
   * Get CSS class for tab
   * @param tabName Tab name
   * @returns CSS class string
   */
  getTabClass(tabName: string): string {
    return this.isTabActive(tabName) ? 'nav-link active' : 'nav-link';
  }

  private loadRequestTabVisibility(): void {
    if (!this.canViewAppraisals) {
      this.hasPendingRequests = false;
      return;
    }

    this.subscriptions.push(
      this.appraisalSettingsService.getMyRequests().subscribe({
        next: requests => this.updatePendingRequestVisibility(requests || []),
        error: () => this.updatePendingRequestVisibility([])
      })
    );
  }

  private updatePendingRequestVisibility(requests: AppraisalAssignmentRequestDto[]): void {
    this.hasPendingRequests = requests.some(
      request => request.Status === AppraisalRelationshipStatus.Pending
    );

    // First time we detect any pending requests in this session, raise the modal so the
    // employee is given explicit instructions and a CTA. We only raise once per session
    // so re-renders (e.g., after the employee approves one of several requests) don't
    // re-pop the modal unnecessarily.
    if (this.hasPendingRequests && !this.pendingPopupShownThisSession) {
      this.showPendingRequestPopup = true;
      this.pendingPopupShownThisSession = true;
    }

    this.applyDefaultTabSelection();
  }

  /** "Review now" — close the popup and jump straight to the Requests tab. */
  reviewPendingRequestsNow(): void {
    this.showPendingRequestPopup = false;
    this.switchTab('requests');
  }

  /** "Later" — just close the popup; the Requests tab is still visible and pre-selected
   *  by applyDefaultTabSelection so they can act when ready. */
  dismissPendingRequestPopup(): void {
    this.showPendingRequestPopup = false;
  }

  isTabAccessible(tabName: string): boolean {
    switch (tabName) {
      case 'list':
        return ( this.canViewAppraisals && (this.isManager || this.hasAppraisalsForCurrentUser));
      case 'my-meetings':
        return ( this.canViewAppraisals && this.isManager && this.hasAppraisalsForCurrentUser
        );
      case 'requests':
        return this.canViewAppraisals && this.hasPendingRequests;
      case 'tasks':
        return this.canViewTasks;
      case 'templates':
      case 'settings':
        return this.canManageTemplates;
      default:
        return false;
    }
  }

  private normalizeEntryTab(tabName: string): string {
    switch (tabName) {
      case 'templates':
      case 'settings':
      case 'requests':
      case 'my-meetings':
      case 'list':
        return tabName;
      case 'tasks':
      default:
        return 'list';
    }
  }

  private getDefaultAccessibleTab(): string {
    const tabOrder = this.isManager
      ? ['list', 'my-meetings', 'requests', 'tasks', 'templates', 'settings']
      : ['list', 'requests', 'tasks', 'templates', 'settings'];
    return tabOrder.find(tab => this.isTabAccessible(tab)) || 'list';
  }

  private applyDefaultTabSelection(): void {
    if (this.hasExplicitTabRequest || this.hasUserSelectedTab) {
      this.enforceTabAccess();
      return;
    }

    // Pending manager-access requests take priority — the employee can't participate
    // in any dialog meeting until they approve/reject, so we surface the Requests tab
    // first. `isTabAccessible('requests')` is already gated on `hasPendingRequests`.
    if (this.isTabAccessible('requests')) {
      this.activeTab = 'requests';
      return;
    }

    if (this.isTabAccessible('list')) {
      this.activeTab = 'list';
      return;
    }

    this.activeTab = this.getDefaultAccessibleTab();
  }
  //#endregion
}
